import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";
import { ApiError } from "./apiError.ts";

export type TelegramLink = components["schemas"]["TelegramLinkDetails"];
export type TelegramProfileKind = components["schemas"]["TelegramProfileKind"];
export type GuardianReachItem = components["schemas"]["GuardianReachItem"];
export type GuardianReachState = components["schemas"]["GuardianReachState"];

export const telegramKeys = {
  all: ["telegram"] as const,
  links: (kind: TelegramProfileKind, ids: readonly string[]) => ["telegram", "links", kind, ids] as const,
  reach: (filters: object) => ["telegram", "reach", filters] as const,
};

/** Active Telegram links of the given profiles (GET /telegram/links, people.read). */
export function useTelegramLinks(kind: TelegramProfileKind, profileIds: readonly string[]) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: telegramKeys.links(kind, profileIds),
    queryFn: async () => {
      const { data, error } = await api.GET("/telegram/links", { params: { query: { ProfileKind: kind, ProfileIds: [...profileIds] } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: profileIds.length > 0,
  });
}

/** Ends a link (POST /telegram/links/{id}/unlink, people.manage) and refreshes every Telegram view. */
export function useUnlinkTelegram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.POST("/telegram/links/{id}/unlink", { params: { path: { id } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: telegramKeys.all });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/** Clears a link's review flag after staff checked it (POST /telegram/links/{id}/confirm, people.manage). */
export function useConfirmTelegramLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.POST("/telegram/links/{id}/confirm", { params: { path: { id } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: telegramKeys.all });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}
